import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import styled, { keyframes } from "styled-components";
import { faSearch, faSync, faTimes } from "@fortawesome/pro-regular-svg-icons";
import Flex from "../Flex/Flex";
import FAIcon from "../Icon/FAIcon";
import InputField from "../InputField/InputField";
import Box from "../Box/Box";
import { Body } from "../Text/Text";

import colors from "../../theme/colors";

import { useDebounce } from "../../hooks/useDebounce";
import OutlinedButton from "../Button/OutlinedButton";

const rotate360 = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const SpinnerIcon = styled(FAIcon)`
  animation: ${(props) => (props.spin ? rotate360 : "")} 1s linear infinite;
  transform: translateZ(0);
`;

// TODO: Move to component folder
export const SearchInput = ({ containerProps, value, placeholder, inputMinWidth, inputWidth, onChange, onSearch, onRefresh, isRefreshing, lastUpdated, refreshText, totalCount = 0, pageCount = 0, showSearchInfoText = true, searchInfoTextContainerStyle = {}, searchInfoText, refreshButtonProps, t, hideRefresh = false, inputStyle = {}, children, childrenContainerStyleProps, ...props }) => {
  const [active, setActive] = useState(false);
  const debouncedValue = useDebounce(value, 400);

  useEffect(() => {
    onSearch(debouncedValue);
  }, [debouncedValue]);

  return (
    <Box flex={[1, null]} {...containerProps}>
      <Flex mt="-1px" alignItems="center" justifyContent="space-between">
        <InputField
          value={value}
          minWidth={inputMinWidth ?? 400}
          width={["100%", inputWidth ?? 400]}
          color="fullShade"
          fontSize={[14, 17]}
          logEventProps={props?.logEventProps}
          onChange={onChange}
          startIcon={<FAIcon icon={faSearch} fontSize={16} color="fullShade" />}
          endIcon={value && <FAIcon icon={faTimes} fontSize={14} style={{ cursor: "pointer" }} onClick={() => onChange({ target: { value: "" } })} />}
          style={{
            outline: "none",
            borderColor: active ? colors.primary : null,
            ...inputStyle,
          }}
          onFocus={() => setActive(true)}
          onBlur={() => setActive(false)}
          placeholder={placeholder || "Search by name or mobile"}
          {...props}
        />
        {children && (
          <Flex flex={1} ml={3} {...childrenContainerStyleProps}>
            {children}
          </Flex>
        )}

        {onRefresh && !hideRefresh && (
          <OutlinedButton
            style={{
              paddingTop: "10px",
              paddingBottom: "10px",
              paddingLeft: "12",
              paddingRight: "12",
            }}
            mr={3}
            borderColor="mediumShade"
            borderRadius={8}
            borderWidth={1}
            onClick={onRefresh}
            {...refreshButtonProps}
          >
            <SpinnerIcon tooltipText={`${t ? t("Last updated") : "Last updated"} ${format(lastUpdated, "dd MMM yyyy, hh:mm a")}`} spin={isRefreshing} icon={faSync} fontSize={16} color="darkestShade" />
          </OutlinedButton>
        )}
      </Flex>

      {showSearchInfoText && (
        <Box py={2} style={searchInfoTextContainerStyle}>
          {!searchInfoText && !value && (
            <Body small fontWeight="400">
              {t ? t("Showing") : "Showing"} {totalCount > 0 ? 1 : 0}-{pageCount} {t ? t("of") : "of"}{" "}
              <b>
                {totalCount} {t ? t("results") : "results"}
              </b>
            </Body>
          )}
          {!searchInfoText && value && (
            <Body small fontWeight="400">
              {t ? t("Showing") : "Showing"} {totalCount > 0 ? 1 : 0}-{pageCount} of{" "}
              <b>
                {totalCount} {t ? t("results for") : "results for"} <i>{value}</i>
              </b>
            </Body>
          )}
          {searchInfoText && (
            <Body small fontWeight="400">
              {searchInfoText}
            </Body>
          )}
        </Box>
      )}
    </Box>
  );
};
